import { useCallback, useEffect, useMemo } from "react";
import type { AnalyticValues } from "@/contexts/analytics/hooks";
import type { NurseData } from "@/utils/models";
import { NURSES_MATRIX_ANALYTICS_SOURCE, type NursesMatrixRowId, type NursesMatrixSelectionMode } from "./consts";

/** The parent-owned selection the grid's `selectionMode` props describe. */
export interface ControlledSelection {
	mode: NursesMatrixSelectionMode;
	selectedRowIds: ReadonlyArray<NursesMatrixRowId>;
	onSelectionChange: (rows: NurseData[]) => void;
}

interface UseControlledSelectionArgs {
	/** `null` = the legacy picker; the hook is then inert (empty set, no-op pick). */
	selection: ControlledSelection | null;
	/** The grid's processed rows (`selectable` already folds in deactivation + `unavailableReason`). */
	rows: NurseData[];
	getRowId: (row: NurseData) => NursesMatrixRowId;
	/** Nurses queued for deny this session — never pickable, matching the legacy guard. */
	queuedDenyIdSet: ReadonlySet<number>;
	/** Moves the grid's keyboard cursor onto the row the user just picked, so arrows continue from there. */
	setCursorRowId: (id: NursesMatrixRowId | null) => void;
	track: (event: string, props?: AnalyticValues) => void;
}

interface ControlledSelectionApi {
	selectedIdSet: ReadonlySet<NursesMatrixRowId>;
	/** Selects the row (single) or toggles it (multiple). Unpickable rows are ignored. */
	pickRow: (row: NurseData) => void;
}

const EMPTY_IDS: ReadonlyArray<NursesMatrixRowId> = [];

/**
 * The controlled half of `NursesMatrixGrid`'s selection modes: turns a pick (row click, Enter, Space) into
 * the parent's next selection and keeps that selection honest — a picked row that stops being pickable
 * (re-evaluated against a new shift, filtered out by a job change) is pruned and reported, so the parent
 * never submits a nurse the grid no longer offers.
 */
export function useControlledSelection({
	selection,
	rows,
	getRowId,
	queuedDenyIdSet,
	setCursorRowId,
	track,
}: UseControlledSelectionArgs): ControlledSelectionApi {
	const mode = selection?.mode;
	// DEFAULT: the legacy picker passes no selection at all; an empty, stable list keeps this hook inert
	// there without a second code path. In the controlled modes `selectedRowIds` is a required prop.
	const selectedRowIds = selection?.selectedRowIds ?? EMPTY_IDS;
	const onSelectionChange = selection?.onSelectionChange;

	const selectedIdSet = useMemo<ReadonlySet<NursesMatrixRowId>>(() => new Set(selectedRowIds), [selectedRowIds]);
	const rowById = useMemo(() => new Map(rows.map((row) => [getRowId(row), row])), [rows, getRowId]);

	const isPickable = useCallback(
		(row: NurseData): boolean => row.selectable !== false && row.isDenied !== true && !queuedDenyIdSet.has(row.id),
		[queuedDenyIdSet],
	);
	const toRows = useCallback(
		(ids: ReadonlyArray<NursesMatrixRowId>) => ids.map((id) => rowById.get(id)).filter((row): row is NurseData => row != null),
		[rowById],
	);

	const pickRow = useCallback(
		(row: NurseData) => {
			if (!mode || !onSelectionChange || !isPickable(row)) return;
			const rowId = getRowId(row);
			setCursorRowId(rowId);
			const wasPicked = selectedIdSet.has(rowId);
			// A radio re-click is a no-op: single mode never clears itself.
			if (mode === "single" && wasPicked) return;
			const nextIds = nextSelectedIds(mode, selectedRowIds, rowId, wasPicked);
			// No `nurse_name`: non-PII identifiers only, matching the legacy select event.
			track("staffing_matrix_select_nurse", {
				source: NURSES_MATRIX_ANALYTICS_SOURCE,
				nurse_id: row.id,
				nurse_role: row.role,
				nurse_type: row.nurseType,
				selection_mode: mode,
				selected: !wasPicked,
				selected_count: nextIds.length,
			});
			onSelectionChange(toRows(nextIds));
		},
		[mode, onSelectionChange, isPickable, getRowId, setCursorRowId, selectedIdSet, selectedRowIds, toRows, track],
	);

	usePruneUnpickable({ selectedRowIds, rowById, isPickable, onSelectionChange, toRows });

	return { selectedIdSet, pickRow };
}

/** The selection after picking `rowId`: the row alone (single), or the row toggled at the end (multiple). */
const nextSelectedIds = (
	mode: NursesMatrixSelectionMode,
	selectedRowIds: ReadonlyArray<NursesMatrixRowId>,
	rowId: NursesMatrixRowId,
	wasPicked: boolean,
): NursesMatrixRowId[] => {
	if (mode === "single") return [rowId];
	return wasPicked ? selectedRowIds.filter((id) => id !== rowId) : [...selectedRowIds, rowId];
};

type PruneArgs = {
	selectedRowIds: ReadonlyArray<NursesMatrixRowId>;
	rowById: ReadonlyMap<NursesMatrixRowId, NurseData>;
	isPickable: (row: NurseData) => boolean;
	onSelectionChange: ((rows: NurseData[]) => void) | undefined;
	toRows: (ids: ReadonlyArray<NursesMatrixRowId>) => NurseData[];
};

/**
 * Prune picks that are no longer on offer. Converges in one round: the parent echoes the pruned ids back,
 * every remaining id is pickable, and nothing fires again.
 */
const usePruneUnpickable = ({ selectedRowIds, rowById, isPickable, onSelectionChange, toRows }: PruneArgs): void => {
	useEffect(() => {
		if (!onSelectionChange) return;
		const kept = selectedRowIds.filter((id) => {
			const row = rowById.get(id);
			return row != null && isPickable(row);
		});
		if (kept.length !== selectedRowIds.length) onSelectionChange(toRows(kept));
	}, [selectedRowIds, rowById, isPickable, onSelectionChange, toRows]);
};
